import type { MediaManagerTemplate } from 'modules/manager/domain/interfaces';
import { TemplateType } from 'modules/manager/domain/template-type';
import type { RenderTemplateArgs } from 'modules/manager/store/render-template-api';

/**
 * Собирает параметры серверного рендера (`POST /api/render-template`) для шаблона,
 * открытого в редакторе, подставляя фикстурные id. Питает локальную кнопку «Рендер»
 * в шапке — в продукте рендер пока ниоткуда не запускается человеком, и кнопка
 * существует ровно для того, чтобы разработчик не собирал тело запроса руками.
 *
 * Id ниже — строки TEST-базы, захардкожены сознательно: кнопка закрыта `useIsLocal`
 * (см. editor-header.tsx), а это компайл-тайм-константа в любом собранном бандле, так
 * что на задеплоенном окружении кнопка не нарисуется ни у кого. Считайте id одноразовыми
 * фикстурами — здесь нет ни одного контракта, перенацеливайте свободно.
 */

/**
 * Самые «полные» данные, найденные на TEST-стенде (10.09.2026): соревнование 757
 * «Чемпионат Бердска» — единственное из 1837 с непустым `images`, его матчи 42380+
 * и этап 5, эмблемы у обеих команд есть. Даже на них рендер отдаёт 500 — см. ловушку №4
 * в `.claude/docs/media-manager-server-render.md`.
 */
const FIXTURE_STAGE_ID = 1430;
const FIXTURE_CHAMPIONSHIP_ID = 1450;
const FIXTURE_MATCH_IDS = [53398, 53654, 53995];

/**
 * Позиционно по регионам layout-блоков — `backgrounds[i]` заполняет регион `i`, лишние
 * сверх числа регионов шаблона сервис рендера игнорирует. Трёх хватает на любой пресет.
 */
const FIXTURE_BG_URLS = [
  'https://images.unsplash.com/photo-1551958219-acbc608c6377?fm=jpg&q=60&w=3000&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
  'https://images.unsplash.com/photo-1517649763962-0c623066013b?fm=jpg&q=60&w=3000&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?fm=jpg&q=60&w=3000&auto=format&fit=crop',
];

/**
 * По одному сборщику на тип — `Record<TemplateType, …>`, чтобы новый тип шаблона не
 * скомпилировался, пока для него не описан режим контекста рендера.
 *
 * Режим ровно один на запрос: на бэкенде ветки сбора контекста идут подряд без `else`,
 * и лишний id молча меняет выбранный режим (см. `buildRenderTemplateBody`).
 */
const PARAM_BUILDERS: Record<TemplateType, (template: MediaManagerTemplate) => RenderTemplateArgs> = {
  [TemplateType.SCHEDULE]: (template) => ({ ...common(template), matchId: FIXTURE_MATCH_IDS[0] }),
  [TemplateType.RESULTS]: (template) => ({ ...common(template), matchId: FIXTURE_MATCH_IDS[0] }),
  [TemplateType.MULTIPLE_SCHEDULE]: (template) => ({ ...common(template), matchIds: [...FIXTURE_MATCH_IDS] }),
  [TemplateType.MULTIPLE_RESULTS]: (template) => ({ ...common(template), matchIds: [...FIXTURE_MATCH_IDS] }),
  [TemplateType.TOURNAMENT_TABLE]: (template) => ({ ...common(template), stageId: FIXTURE_STAGE_ID }),
  [TemplateType.TOP_PLAYERS_BY_INDICATORS]: (template) => ({ ...common(template), championshipId: FIXTURE_CHAMPIONSHIP_ID }),
};

/** Общая часть любого режима: сам шаблон, его подтипы и фоны регионов. */
function common(template: MediaManagerTemplate): RenderTemplateArgs {
  return {
    templateId: template.id,
    templateSubtypes: [...template.templateSubtypes],
    backgrounds: [...FIXTURE_BG_URLS],
  };
}

export function buildDevRenderParams(template: MediaManagerTemplate): RenderTemplateArgs {
  return PARAM_BUILDERS[template.templateType](template);
}
